'use client'
import {
  isPasswordValid
} from "./chunk-SQJGLUQL.mjs";
import {
  __async,
  __objRest,
  __spreadProps,
  __spreadValues
} from "./chunk-UYSR6ZT4.mjs";

// src/components/reset-by-token/ResetByToken.tsx
import { useState } from "react";
import { twMerge } from "tailwind-merge";
import { Button as Button3, Paper, Spinner, Typography as Typography3 } from "@wbk/ui";
import { useVerifyPasswordResetToken } from "@wbk/api/hooks";
import { useRouter, useTranslation as useTranslation4 } from "@wbk/config";

// src/components/reset-by-token/ResetByTokenForm.tsx
import { Controller, useForm } from "react-hook-form";
import { AnimatePresence, motion } from "motion/react";
import { Button, Input, Typography } from "@wbk/ui";
import { useResetPasswordWithToken } from "@wbk/api/hooks";
import { useTranslation as useTranslation2 } from "@wbk/config";
import { useAnalytics } from "@wbk/analytics";

// src/components/bullets/PasswordBullets.tsx
import { CheckIcon, CloseIcon } from "@wbk/svg";
import { useTranslation } from "@wbk/config";
import { jsx, jsxs } from "react/jsx-runtime";
var PasswordBullets = ({ password = "", error }) => {
  const { t } = useTranslation(["auth_common"]);
  const isValid = isPasswordValid(password);
  return /* @__PURE__ */ jsx("ul", { className: "mb-4 space-y-1 text-sm", children: /* @__PURE__ */ jsxs(
    "li",
    {
      className: `flex items-start gap-1 ${isValid ? "text-primary-success" : error ? "text-primary-red" : ""}`,
      children: [
        isValid ? /* @__PURE__ */ jsx(CheckIcon, { className: "[&>path]:fill-body mt-0.5 h-5 w-8 text-green-500" }) : /* @__PURE__ */ jsx(CloseIcon, { className: "mt-0.5 h-5 w-8 rounded-full text-red-500" }),
        /* @__PURE__ */ jsx("span", { children: t("auth_common:validation.password_validation") })
      ]
    }
  ) });
};
var PasswordBullets_default = PasswordBullets;

// src/components/reset-by-token/ResetByTokenForm.tsx
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var ResetByTokenForm = ({ email, token, onSuccess }) => {
  var _a, _b;
  const { t } = useTranslation2(["auth_common"]);
  const { trackEvent } = useAnalytics();
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid }
  } = useForm({ defaultValues: { password: "", password_confirmation: "" } });
  const { mutateAsync: resetPassword, isPending, error } = useResetPasswordWithToken();
  const password = watch("password");
  const passwordErrors = (_a = error == null ? void 0 : error.errors) == null ? void 0 : _a.password;
  const confirmErrors = (_b = error == null ? void 0 : error.errors) == null ? void 0 : _b.password_confirmation;
  const onSubmit = (values) => __async(null, null, function* () {
    try {
      yield resetPassword(__spreadValues({ email, token }, values));
      trackEvent({ event: "password_reset_completed" });
      onSuccess();
    } catch (err) {
      trackEvent({
        event: "Reset Password - Reset Password Failed",
        // domain only, the full address shouldn't reach a tracker
        data: { email_domain: email.split("@")[1], error: err }
      });
    }
  });
  return /* @__PURE__ */ jsxs2("form", { id: "reset-by-token-form", onSubmit: handleSubmit(onSubmit), className: "space-y-5", children: [
    /* @__PURE__ */ jsxs2("div", { className: "space-y-1 text-center", children: [
      /* @__PURE__ */ jsx2(Typography, { as: "h2", variant: "heading-M", children: t("auth_common:reset_password_title") }),
      /* @__PURE__ */ jsx2(Typography, { variant: "body-S", color: "text-secondary", children: t("auth_common:password_description") })
    ] }),
    /* @__PURE__ */ jsxs2("div", { className: "space-y-1", children: [
      /* @__PURE__ */ jsx2(Typography, { variant: "label-M", color: "text-secondary", children: t("auth_common:email") }),
      /* @__PURE__ */ jsx2(Typography, { variant: "body-M", "data-testid": "auth_reset_link_email", children: email })
    ] }),
    /* @__PURE__ */ jsx2(
      Controller,
      {
        control,
        name: "password",
        rules: {
          required: t("auth_common:validation.required"),
          validate: (value) => isPasswordValid(value || "")
        },
        render: (_c) => {
          var { field: _d } = _c, _e = _d, { onChange } = _e, field = __objRest(_e, ["onChange"]);
          var _a2;
          return /* @__PURE__ */ jsx2(
            Input,
            __spreadProps(__spreadValues({}, field), {
              type: "password",
              "data-testid": "auth_new_password_input",
              label: t("auth_common:new_password"),
              onChange: (value) => onChange(value),
              error: ((_a2 = errors.password) == null ? void 0 : _a2.message) || !!(passwordErrors == null ? void 0 : passwordErrors.length)
            })
          );
        }
      }
    ),
    !!(passwordErrors == null ? void 0 : passwordErrors.length) && /* @__PURE__ */ jsx2("div", { className: "space-y-1", children: passwordErrors.map((err) => /* @__PURE__ */ jsx2(Typography, { color: "error", variant: "body-S", children: err }, err)) }),
    /* @__PURE__ */ jsx2(PasswordBullets_default, { password }),
    /* @__PURE__ */ jsx2(AnimatePresence, { children: isPasswordValid(password) && /* @__PURE__ */ jsxs2(
      motion.div,
      {
        className: "overflow-hidden",
        initial: { height: 0 },
        animate: { height: "auto" },
        exit: { height: 0 },
        children: [
          /* @__PURE__ */ jsx2(
            Controller,
            {
              control,
              name: "password_confirmation",
              rules: {
                required: t("auth_common:validation.required"),
                validate: (value) => value === password || t("auth_common:validation.passwords_not_match")
              },
              render: (_f) => {
                var { field: _g } = _f, _h = _g, { onChange } = _h, field = __objRest(_h, ["onChange"]);
                var _a2;
                return /* @__PURE__ */ jsx2(
                  Input,
                  __spreadProps(__spreadValues({}, field), {
                    type: "password",
                    autoComplete: "off",
                    onPaste: (e) => e.preventDefault(),
                    "data-testid": "auth_confirm_new_password_input",
                    label: t("auth_common:confirm_password"),
                    onChange: (value) => onChange(value),
                    error: ((_a2 = errors.password_confirmation) == null ? void 0 : _a2.message) || !!(confirmErrors == null ? void 0 : confirmErrors.length)
                  })
                );
              }
            }
          ),
          !!(confirmErrors == null ? void 0 : confirmErrors.length) && /* @__PURE__ */ jsx2("div", { className: "space-y-1", children: confirmErrors.map((err) => /* @__PURE__ */ jsx2(Typography, { color: "error", variant: "body-S", children: err }, err)) })
        ]
      }
    ) }),
    typeof (error == null ? void 0 : error.message) === "string" && error.message !== "[object Object]" && /* @__PURE__ */ jsx2(Typography, { color: "error", variant: "body-S", className: "text-center", children: error.message }),
    /* @__PURE__ */ jsx2(
      Button,
      {
        type: "submit",
        className: "w-full",
        loading: isPending,
        disabled: !isValid,
        "data-testid": "auth_update_password_button",
        children: /* @__PURE__ */ jsx2(Typography, { variant: "label-M", children: t("auth_common:update_password") })
      }
    )
  ] });
};
var ResetByTokenForm_default = ResetByTokenForm;

// src/components/reset-by-token/ResetByTokenSuccess.tsx
import { Button as Button2, Typography as Typography2 } from "@wbk/ui";
import { AnimatedCheck } from "@wbk/svg";
import { useTranslation as useTranslation3 } from "@wbk/config";
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var ResetByTokenSuccess = ({ onDone }) => {
  const { t } = useTranslation3(["auth_common"]);
  return /* @__PURE__ */ jsxs3("div", { className: "flex flex-col items-center gap-6 py-4 text-center", children: [
    /* @__PURE__ */ jsx3(AnimatedCheck, { className: "text-success h-24 w-24" }),
    /* @__PURE__ */ jsxs3("div", { className: "space-y-1", children: [
      /* @__PURE__ */ jsx3(Typography2, { as: "h2", variant: "heading-M", children: t("auth_common:password_updated") }),
      /* @__PURE__ */ jsx3(Typography2, { variant: "body-M", color: "text-secondary", children: t("auth_common:password_updated_description") })
    ] }),
    /* @__PURE__ */ jsx3(Button2, { "data-testid": "auth_to_login_after_reset_button", className: "w-full", onClick: onDone, children: /* @__PURE__ */ jsx3(Typography2, { variant: "label-L", children: t("auth_common:login") }) })
  ] });
};
var ResetByTokenSuccess_default = ResetByTokenSuccess;

// src/components/reset-by-token/ResetByToken.tsx
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
var Notice = ({
  testId,
  title,
  children
}) => /* @__PURE__ */ jsxs4("div", { className: "space-y-4 py-16 text-center", "data-testid": testId, children: [
  /* @__PURE__ */ jsx4(Typography3, { variant: "heading-M", color: "error", children: title }),
  children
] });
var ResetByToken = ({ token, className }) => {
  const { t } = useTranslation4(["auth_common"]);
  const { push, locale } = useRouter();
  const [done, setDone] = useState(false);
  const { data, isPending, error, refetch } = useVerifyPasswordResetToken({ token });
  const goToLogin = () => push(`/${locale}/login`);
  const linkIsDead = error ? error.status === 422 : !(data == null ? void 0 : data.email);
  return /* @__PURE__ */ jsx4(Paper, { className: twMerge("bg-paper overflow-hidden", className), children: isPending ? /* @__PURE__ */ jsx4("div", { className: "flex h-80 items-center justify-center", children: /* @__PURE__ */ jsx4(Spinner, { width: 100, className: "text-primary" }) }) : done ? /* @__PURE__ */ jsx4(ResetByTokenSuccess_default, { onDone: goToLogin }) : (data == null ? void 0 : data.email) ? /* @__PURE__ */ jsx4(ResetByTokenForm_default, { email: data.email, token, onSuccess: () => setDone(true) }) : linkIsDead ? /* @__PURE__ */ jsx4(Notice, { testId: "reset_password_invalid_link", title: t("auth_common:reset_link.invalid"), children: /* @__PURE__ */ jsx4(Typography3, { variant: "body-M", color: "text-secondary", children: t("auth_common:reset_link.invalid_description") }) }) : /* @__PURE__ */ jsx4(Notice, { testId: "reset_password_unavailable", title: t("auth_common:error.unknown_error"), children: /* @__PURE__ */ jsx4(Button3, { onClick: () => refetch(), "data-testid": "reset_password_retry", children: t("auth_common:reset_link.try_again") }) }) });
};

export {
  PasswordBullets_default,
  ResetByToken
};
//# sourceMappingURL=chunk-LQCB7533.mjs.map